/**
 * FauxDS IconButton - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate IconButton component for prototyping.
 * It is NOT the real @faire/slate IconButton and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate IconButton, see: @faire/slate/components/icon-button
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 26445-9624 (Slate Icon Button)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Primary, Secondary, Tertiary
 * - All sizes verified: XXSmall (24px), XSmall (32px), Small (40px), Medium (48px)
 * - All shapes verified: Circle, Square
 * - All states verified: Default, Hover, Active, Disabled, Loading
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef } from "react";

import LoadingSpinner from "./LoadingSpinner";

export type IconButtonVariant = "primary" | "secondary" | "tertiary";
export type IconButtonShape = "circle" | "square";
export type IconButtonSize = "xxSmall" | "xSmall" | "small" | "medium";

export interface IconButtonProps {
  /**
   * The icon to display (ReactNode)
   */
  Icon: React.FC<any>;
  /**
   * Button variant
   * @default "primary"
   */
  variant?: IconButtonVariant;
  /**
   * Button shape
   * @default "circle"
   */
  shape?: IconButtonShape;
  /**
   * Button size
   * xxSmall (24px), xSmall (32px), small (40px), medium (48px)
   * @default "medium"
   */
  size?: IconButtonSize;
  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Whether the button is in loading state
   * @default false
   */
  loading?: boolean;
  /**
   * Click handler
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Accessible label (required for icon-only buttons)
   */
  "aria-label"?: string;

  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

// Size to pixel mapping (from Figma)
const sizeMap: Record<IconButtonSize, number> = {
  xxSmall: 24,
  xSmall: 32,
  small: 40,
  medium: 48,
};

// Size to icon size mapping (from Figma)
const iconSizeMap: Record<IconButtonSize, number> = {
  xxSmall: 12,
  xSmall: 16,
  small: 20,
  medium: 24,
};

// Size to spinner size mapping
const spinnerSizeMap: Record<IconButtonSize, "xSmall" | "small" | "medium"> = {
  xxSmall: "xSmall",
  xSmall: "small",
  small: "small",
  medium: "medium",
};

/**
 * IconButton - FauxDS (recreated from Figma MCP)
 *
 * Icon buttons enable users to take action or make a choice in smaller, refined spaces.
 * This implementation was created by extracting design specs directly from Figma.
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      Icon,
      variant = "primary",
      shape = "circle",
      size = "medium",
      disabled = false,
      loading = false,
      onClick,
      "aria-label": ariaLabel,
      className,
    },
    ref
  ) => {
    const pixelSize = sizeMap[size];
    const iconSize = iconSizeMap[size];
    const spinnerSize = spinnerSizeMap[size];

    // Determine visual state styles based on Figma design tokens
    const getVariantStyles = () => {
      if (disabled) {
        return {
          bg: "bg-[var(--slate-color-action-surface-disabled)]", // #f7f7f7
          border:
            variant === "secondary"
              ? "border-[var(--slate-color-action-border-disabled)]"
              : "",
          iconColor: "text-[var(--slate-color-action-icon-disabled)]", // #757575
          hoverBg: "",
          activeBg: "",
        };
      }

      switch (variant) {
        case "primary":
          return {
            bg: "bg-[var(--slate-color-action-surface-default)]", // #333333
            border: "",
            iconColor: "text-[var(--slate-color-action-icon-inverse)]", // #ffffff
            hoverBg: "hover:bg-[var(--slate-color-action-surface-hover)]", // #000000
            activeBg: "active:bg-[var(--slate-color-action-surface-active)]", // #000000
          };
        case "secondary":
          return {
            bg: "bg-[var(--slate-color-action-surface-inverse)]", // #ffffff
            border: "border border-[var(--slate-color-action-border-subdued)]", // #dfe0e1
            iconColor: "text-[var(--slate-color-action-icon-default)]", // #333333
            hoverBg:
              "hover:bg-[var(--slate-color-action-surface-inverse-hover)]", // #f7f7f7
            activeBg:
              "active:bg-[var(--slate-color-action-surface-inverse-active)]", // #f7f7f7
          };
        case "tertiary":
          return {
            bg: "bg-transparent",
            border: "",
            iconColor: "text-[var(--slate-color-action-icon-default)]", // #333333
            hoverBg:
              "hover:bg-[var(--slate-color-action-surface-inverse-hover)]", // #f7f7f7
            activeBg:
              "active:bg-[var(--slate-color-action-surface-inverse-active)]", // #f7f7f7
          };
      }
    };

    const variantStyles = getVariantStyles();

    // Shape styles
    const shapeStyles: Record<IconButtonShape, string> = {
      circle: "rounded-full",
      square: "rounded-[var(--slate-radius-component-default)]", // 4px
    };

    // Focus ring styles
    const focusRingStyles =
      "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--slate-color-action-border-focus-ring)]";

    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={disabled || loading}
        aria-label={ariaLabel}
        aria-busy={loading}
        className={cn(
          "relative inline-flex shrink-0 items-center justify-center transition-colors duration-150 ease-in-out",
          focusRingStyles,
          "disabled:cursor-not-allowed",
          variantStyles.bg,
          variantStyles.border,
          variantStyles.iconColor,
          !disabled && !loading ? variantStyles.hoverBg : "",
          !disabled && !loading ? variantStyles.activeBg : "",
          shapeStyles[shape],
          className
        )}
        style={{
          width: pixelSize,
          height: pixelSize,
          minWidth: pixelSize,
        }}
      >
        {/* Loading spinner or icon */}
        {loading ? (
          <span className="[&_svg]:text-inherit!">
            <LoadingSpinner size={spinnerSize} aria-label="Loading" />
          </span>
        ) : (
          <span
            className="flex items-center justify-center"
            style={{
              width: iconSize,
              height: iconSize,
            }}
          >
            <Icon />
          </span>
        )}
      </button>
    );
  }
);

IconButton.displayName = "IconButton";

export default IconButton;
